import { FEATURES, SEARCH_GROUPS } from '@constants/Text'
import { ehEscritorioInternoAawz } from '@modules/solicitacoes/utils/escritorioInterno'
import { CLASSIFICACAO_BACKOFFICE } from '@modules/solicitacoes/constants/classificacao'

// Para onde `/wealth-service/backoffice` manda quem entra sem sub-rota.
//
// O time interno cai na FILA (a tela de trabalho: o que precisa ser feito hoje);
// todo mundo mais cai na Matriz, como sempre. Mandar o interno para a Matriz
// seria abrir a área numa tela de acompanhamento e obrigar um clique a mais
// justamente em quem usa isto o dia inteiro.
//
// `require` lazy do store, e não import no topo: `routes.js` é avaliado no boot
// do router, ANTES de o store montar — mesmo padrão de
// `redirectSolicitacoesRoot` em `modules/solicitacoes/routes.js`.

// Gate compartilhado por TODA a área de Backoffice do WS. `customPermission` é
// avaliado pelo guard global do router; sem `can_show`, porque a pergunta não é
// "contratou?" e sim "é o time que opera isto?".
export const BACKOFFICE_META = {
  customPermission: ({ user }) => ehEscritorioInternoAawz(user),
}

// Contexto de esteira compartilhado pela fila e pelo seu detalhe (spec-002,
// US-1/US-2): a fila do WS SEMPRE abre na esteira Backoffice — diferente da
// Central de Solicitações do Hub, que abre em ESTRATÉGICO — e o clique numa
// linha vai para o detalhe DENTRO do WS, não para `/solicitacoes/operacao-aawz`.
// Definido uma vez e reusado nas duas rotas para as views (via
// `resolverContextoMontagem`, em `modules/solicitacoes/utils/`) nunca lerem um
// contexto divergente entre lista e detalhe.
export const FILA_BACKOFFICE_CONTEXTO = {
  esteiraFixa: CLASSIFICACAO_BACKOFFICE,
  rotaDetalhe: 'ws.backoffice.fila.detalhe',
  rotaLista: 'ws.backoffice.fila',
  carregarAoMontar: true,
  // T009 (US-7): o detalhe do WS mostra a conversa com o consultor — aqui é o
  // time interno RESPONDENDO. No Hub o chat não entra: lá a Comunicação já é a
  // inbox própria da solicitação (rota `solicitacoes.operacao-aawz.comunicacao`).
  chat: true,
}

export const redirectBackofficeRoot = () => {
  const store = require('@state/store').default
  const getters = (store && store.getters) || {}
  const user = getters['user/currentUser'] || {}
  return ehEscritorioInternoAawz(user)
    ? { name: 'ws.backoffice.fila' }
    : { name: 'ws.backoffice.matriz' }
}

export default [
  {
    path: '/wealth-service',
    name: 'ws',
    meta: {
      can_show: FEATURES.WS,
      search: {
        title: 'Wealth Service',
        description: 'Gestão de clientes, ativos e operações do wealth',
        keywords: ['wealth service', 'clientes', 'ativos', 'operações'],
        icon: 'hierarchy',
        group: SEARCH_GROUPS.GESTAO_PORTFOLIOS,
      },
    },
    redirect: '/wealth-service/clientes',
    component: () => import('@modules/ws/views/ws-nav-bar.vue'),
    children: [
      {
        path: 'clientes',
        name: 'ws.clients',
        meta: {
          can_show: FEATURES.WS,
        },
        component: () => import('@modules/ws/views/view-clients.vue'),
      },
      {
        path: 'clientes/:codigo',
        name: 'ws.clients.codigo',
        meta: {
          can_show: FEATURES.WS,
        },
        component: () => import('@modules/ws/views/view-client-wallet.vue'),
      },
      {
        path: 'clientes/:codigo/conta',
        name: 'ws.clients.account',
        meta: {
          can_show: FEATURES.WS,
        },
        component: () => import('@modules/ws/views/view-client-account.vue'),
      },
      {
        path: 'clientes/:codigo/extrato',
        name: 'ws.clients.extract',
        meta: {
          can_show: FEATURES.WS,
        },
        component: () => import('@modules/ws/views/view-client-extract.vue'),
      },
      {
        path: 'ativos',
        name: 'ws.ativos',
        meta: {
          can_show: FEATURES.WS,
          search: {
            title: 'Ativos do Wealth Service',
            description: 'Catálogo de ativos e instrumentos do wealth',
            keywords: ['ativos', 'produtos', 'instrumentos', 'catálogo'],
          },
        },
        component: () => import('@modules/ws/views/view-assets.vue'),
      },
      {
        path: 'operacoes',
        name: 'ws.operations',
        meta: {
          can_show: FEATURES.WS,
          search: {
            title: 'Operações do Wealth Service',
            description: 'Ordens e operações do wealth service',
            keywords: ['operações', 'ordens', 'movimentações', 'boletas'],
          },
        },
        component: () => import('@modules/ws/views/view-operations.vue'),
      },
      {
        path: 'carteiras-recomendadas',
        name: 'ws.recommended-portfolios',
        meta: {
          can_show: FEATURES.WS,
          search: {
            title: 'Carteiras recomendadas',
            description: 'Carteiras modelo recomendadas do wealth service',
            keywords: ['carteiras', 'recomendadas', 'modelos', 'alocação'],
          },
        },
        component: () =>
          import('@modules/ws/views/view-recommended-portfolios.vue'),
      },
      {
        path: 'backoffice',
        name: 'ws.backoffice',
        // Gate por ESCRITÓRIO, e não por feature (decisão do usuário,
        // 28/08/2026): a área de Backoffice é do time interno — 2976 e o de
        // teste 83 — e nunca de escritório parceiro. Uma feature flag modela
        // "quem contratou", que não é a pergunta aqui: seria dado por
        // escritório para uma resposta que já está no código, e alguém teria de
        // lembrar de ligá-la. `ehEscritorioInternoAawz` é a mesma fonte que o
        // backend usa (`ESCRITORIOS_BACKOFFICE`).
        meta: {
          ...BACKOFFICE_META,
        },
        redirect: redirectBackofficeRoot,
        component: () =>
          import('@modules/ws/views/backoffice/view-backoffice.vue'),
        children: [
          {
            path: 'matriz',
            name: 'ws.backoffice.matriz',
            meta: {
              ...BACKOFFICE_META,
            },
            component: () =>
              import('@modules/ws/views/backoffice/view-backoffice-matrix.vue'),
          },
          {
            // Path mais específico ANTES de 'onboarding' (boa prática de
            // ordenação — path-to-regexp faz match exato em ambos, sem
            // wildcard, então a ordem não afeta a resolução de fato; ver
            // routes.unit.js).
            path: 'onboarding/nova-abertura',
            name: 'ws.backoffice.onboarding-form',
            meta: {
              ...BACKOFFICE_META,
            },
            component: () =>
              import('@modules/ws/views/backoffice/view-onboarding-form.vue'),
          },
          {
            path: 'onboarding',
            name: 'ws.backoffice.onboarding',
            meta: {
              ...BACKOFFICE_META,
            },
            component: () =>
              import('@modules/ws/views/backoffice/view-onboarding.vue'),
          },
          {
            path: 'migracoes',
            name: 'ws.backoffice.migracoes',
            meta: {
              ...BACKOFFICE_META,
            },
            component: () =>
              import(
                '@modules/ws/views/backoffice/view-account-migrations.vue'
              ),
          },
          {
            path: 'custodia',
            name: 'ws.backoffice.custodia',
            meta: {
              ...BACKOFFICE_META,
            },
            component: () =>
              import(
                '@modules/ws/views/backoffice/view-custody-transfers.vue'
              ),
          },
          {
            path: 'preco-medio',
            name: 'ws.backoffice.preco-medio',
            meta: {
              ...BACKOFFICE_META,
            },
            component: () =>
              import('@modules/ws/views/backoffice/view-average-price.vue'),
          },
          {
            // Cobranças CVM — a MESMA view do módulo `billing-system`
            // (`/cobrancas`), referenciada aqui para o backoffice não precisar
            // sair do WS. As duas rotas seguem no ar em paralelo, apontando
            // para um único arquivo: não há cópia de código nem segundo store
            // (o de `billingSystem` é registrado no boot, em `modules/index.js`,
            // e não depende do shell do módulo de origem).
            //
            // Gate DUPLO de propósito: o `BACKOFFICE_META` libera a área para
            // o time que a opera, mas quem não contratou Cobranças CVM não pode
            // ver o dado só por estar no WS. São eixos independentes — um
            // `customPermission` (quem opera) e um `can_show` (quem contratou),
            // ambos avaliados pelo guard global.
            path: 'cobrancas',
            name: 'ws.backoffice.cobrancas',
            meta: {
              ...BACKOFFICE_META,
              // Feature PRÓPRIA continua valendo: estar no time interno não
              // implica ter contratado Cobranças CVM.
              can_show: FEATURES.COBRANCAS_CVM,
            },
            component: () =>
              import(
                '@src/modules/billing-system/views/view-billing-system-overview/view-billing-system-overview.vue'
              ),
          },
          {
            // Fila de trabalho do time interno, DENTRO do WS (P0.10, spec-002).
            //
            // Reusa a MESMA view da Operação AAWZ em vez de copiar — é a fila
            // de solicitações de todos os escritórios parceiros, auto-contida,
            // sem depender do shell de origem. Agora abre fixa na esteira
            // Backoffice (`FILA_BACKOFFICE_CONTEXTO`) e o clique numa linha
            // leva ao detalhe DENTRO do WS (`ws.backoffice.fila.detalhe`
            // abaixo) — não mais a `/solicitacoes/operacao-aawz`.
            path: 'fila',
            name: 'ws.backoffice.fila',
            meta: {
              ...BACKOFFICE_META,
              solicitacoesContexto: FILA_BACKOFFICE_CONTEXTO,
              search: {
                title: 'Fila de Trabalho — Backoffice WS',
                description:
                  'Solicitações a atender pelo time de backoffice do Wealth Service',
                keywords: ['fila', 'backoffice', 'solicitações', 'wealth service'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
            component: () =>
              import(
                '@modules/solicitacoes/views/operacao-aawz/view-operacao-aawz-visao-geral.vue'
              ),
          },
          {
            // Detalhe de uma solicitação da fila, DENTRO do WS (spec-002,
            // US-1/US-2). Sem `meta.search`: segue o padrão das demais rotas
            // dinâmicas do módulo (`ws.solicitacoes.detalhe`, `ws.clients.*`),
            // que não indexam a busca global por não terem título fixo.
            path: 'fila/:id',
            name: 'ws.backoffice.fila.detalhe',
            meta: {
              ...BACKOFFICE_META,
              solicitacoesContexto: FILA_BACKOFFICE_CONTEXTO,
            },
            component: () =>
              import(
                '@modules/solicitacoes/views/operacao-aawz/view-operacao-aawz-detalhe.vue'
              ),
          },
          {
            // Fee Billing (cobrança de fee por lote) — NÃO é Cobranças CVM.
            // Morava em `backoffice/cobrancas` até 24/08/2026, quando aquela
            // rota passou a servir a Cobranças CVM real. Ganhou caminho próprio
            // em vez de ser removida: o subsistema segue vivo e alimentando a
            // Matriz do Backoffice, os indicadores da Visão da Base e a coluna
            // `cobranca` do `client-process-matrix` — apagar a rota deixaria
            // esse dado circulando sem tela que o mostre.
            // Continua sem backend (ver pending-tasks P0.1).
            path: 'fee-billing',
            name: 'ws.backoffice.fee-billing',
            meta: {
              ...BACKOFFICE_META,
            },
            component: () =>
              import('@modules/ws/views/backoffice/view-fee-billing.vue'),
          },
          {
            path: 'profissionais',
            name: 'ws.backoffice.profissionais',
            meta: {
              ...BACKOFFICE_META,
            },
            component: () =>
              import('@modules/ws/views/backoffice/view-professionals.vue'),
          },
          {
            path: 'troca-clientes',
            name: 'ws.backoffice.client-swap',
            meta: {
              ...BACKOFFICE_META,
            },
            component: () =>
              import('@modules/ws/views/backoffice/view-client-swap.vue'),
          },
          {
            path: 'cashback',
            name: 'ws.backoffice.cashback',
            meta: {
              ...BACKOFFICE_META,
            },
            component: () =>
              import('@modules/ws/views/backoffice/view-cashback.vue'),
          },
        ],
      },
      {
        // Esteira de solicitacoes Backoffice: o consultor abre o pedido para o
        // time interno da AAWZ (escritorio 2976) e acompanha aqui. Substitui a
        // aba "Suporte", que era o mock embrionario desta mesma esteira.
        path: 'solicitacoes',
        name: 'ws.solicitacoes',
        meta: {
          can_show: FEATURES.WS,
        },
        component: () => import('@modules/ws/views/view-ws-solicitacoes.vue'),
      },
      {
        // Path mais específico ANTES de 'solicitacoes' — mesma boa prática
        // de ordenação já aplicada a 'onboarding/nova-abertura' acima (não
        // afeta a resolução, path-to-regexp casa exato em ambos; ver
        // routes.unit.js).
        path: 'solicitacoes/nova',
        name: 'ws.solicitacoes.nova',
        meta: {
          can_show: FEATURES.WS,
        },
        component: () =>
          import('@modules/ws/views/view-ws-solicitacao-nova.vue'),
      },
      {
        // Path dinâmico — precisa vir DEPOIS de 'solicitacoes/nova' na ordem
        // do array. Diferente do caso 'onboarding/nova-abertura' (dois paths
        // estáticos, ordem não importa), aqui ':id' É um segmento dinâmico:
        // se viesse antes, capturaria '/solicitacoes/nova' como id='nova'
        // (vue-router 3 resolve por match linear na ordem declarada). Coberto
        // em routes.unit.js.
        path: 'solicitacoes/:id',
        name: 'ws.solicitacoes.detalhe',
        meta: {
          can_show: FEATURES.WS,
        },
        component: () =>
          import('@modules/ws/views/view-ws-solicitacao-detalhe.vue'),
      },
      {
        path: 'dashboard',
        name: 'ws.dashboard',
        meta: {
          can_show: FEATURES.WS,
        },
        component: () => import('@modules/ws/views/view-base-dashboard.vue'),
      },
      // Épico futuro (A6 — produtos/prateleira por instituição). Reserva de
      // espaço na navegação/arquitetura; sem componente até o épico entrar
      // em desenvolvimento.
      // {
      //   path: 'produtos',
      //   name: 'ws.produtos',
      //   meta: { can_show: FEATURES.WS },
      //   component: () => import('@modules/ws/views/view-products.vue'),
      // },
    ],
  },
]
